import { isDefined } from '#misc';

/**
 * A type-safe wrapper around the browser's native {@link BroadcastChannel} API.
 *
 * Provides a typed `post` method and message handler while silently no-oping in
 * environments where `BroadcastChannel` is unavailable (e.g., SSR, older browsers).
 *
 * Each instance represents a persistent, open channel — it continues to receive messages
 * until {@link TypedBroadcastChannel.close} is called. For one-off broadcasting without
 * a persistent listener, use {@link broadcastOnce} instead.
 *
 * @template TMessage - The shape of messages exchanged on this channel.
 *
 * @example
 * ```ts
 * const channel = new TypedBroadcastChannel<AuthMessage>('datastream:auth', (message) => {
 *   if (message.type === 'logout') {
 *     handleLogout();
 *   }
 * });
 *
 * // Later, when done:
 * channel.close();
 * ```
 */
export class TypedBroadcastChannel<TMessage> {
  private readonly channel: BroadcastChannel | null;

  private readonly channelName: string;

  /**
   * Creates a new typed broadcast channel.
   *
   * @param name - The channel name. All instances in the same origin sharing this name
   *   can communicate with each other.
   * @param onMessage - Optional handler invoked for each message received on this channel.
   *   Not called for messages posted by this same instance.
   */
  constructor(name: string, onMessage?: (message: TMessage) => void) {
    this.channelName = name;

    if (!TypedBroadcastChannel.isSupported) {
      this.channel = null;

      return;
    }

    this.channel = new BroadcastChannel(name);

    if (isDefined(onMessage)) {
      this.channel.onmessage = ({ data }: MessageEvent<TMessage>) => {
        onMessage(data);
      };
    }
  }

  /**
   * Whether the `BroadcastChannel` API is available in the current environment.
   * Returns `false` in SSR contexts or browsers that lack the API.
   */
  static get isSupported(): boolean {
    return isDefined(globalThis.BroadcastChannel);
  }

  /**
   * The name of the underlying broadcast channel.
   */
  get name(): string {
    return this.channelName;
  }

  /**
   * Closes the channel and releases browser resources.
   * After calling this, the instance no longer receives or sends messages.
   */
  close(): void {
    this.channel?.close();
  }

  /**
   * Broadcasts a message to all other instances listening on the same channel name.
   * Has no effect if the channel has been closed or if the API is unsupported.
   *
   * @param message - The typed message to broadcast.
   */
  post(message: TMessage): void {
    this.channel?.postMessage(message);
  }
}
